import React, { useState } from 'react';
import { generateSQL, SQLGenerationResult } from './api';
import DebugPanel from './DebugPanel';
import SQLEditor, { SchemaMeta } from './SQLEditor';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { Upload, Database, Sparkles, Copy, RotateCcw, AlertCircle, CheckCircle, FileText, Settings } from 'lucide-react';

// Debug logging for deployment
console.log('App.tsx loaded successfully');
console.log('Environment:', {
  hostname: window.location.hostname,
  href: window.location.href,
  MODE: import.meta.env.MODE || 'unknown'
});

const SQL_DIALECTS = [
  { value: 'postgres', label: 'PostgreSQL' },
  { value: 'mysql', label: 'MySQL' },
  { value: 'sqlite', label: 'SQLite' },
  { value: 'mssql', label: 'SQL Server' },
  { value: 'bigquery', label: 'BigQuery' },
  { value: 'oracle', label: 'Oracle' },
];

const LIMIT_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '250', label: '250' },
  { value: '500', label: '500' },
  { value: '1000', label: '1000' },
  { value: 'all', label: 'All (No Limit)' },
  { value: 'custom', label: 'Custom' },
];

function App() {
  const [schemaFile, setSchemaFile] = useState<File | null>(null);
  const [request, setRequest] = useState('');
  const [dialect, setDialect] = useState('postgres');
  const [limit, setLimit] = useState('100');
  const [customLimit, setCustomLimit] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SQLGenerationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editedSQL, setEditedSQL] = useState<string>('');
  const [isSQLValid, setIsSQLValid] = useState<boolean>(true);
  const [sqlValidationErrors, setSQLValidationErrors] = useState<string[]>([]);
  const [schemaMeta, setSchemaMeta] = useState<SchemaMeta | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSchemaFile(file);
      setError(null);
      // Parse for metadata (lightweight, no external lib) first ~200 lines
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = String(reader.result || '');
          const lines = text.split(/\r?\n/).filter(Boolean).slice(0, 500);
          if (lines.length < 2) return;
          // Try to detect headers
          const header = lines[0].toLowerCase().split(',').map(h => h.trim());
          const idxTable = header.findIndex(h => /table/.test(h));
          const idxColumn = header.findIndex(h => /column/.test(h));
          const idxType = header.findIndex(h => /type|data_type|datatype/.test(h));
          if (idxTable === -1 || idxColumn === -1) return; // Not enough info
          const tables: SchemaMeta['tables'] = {};
          for (let i = 1; i < lines.length; i++) {
            const parts = lines[i].split(',');
            if (parts.length < 2) continue;
            const t = parts[idxTable]?.trim();
            const c = parts[idxColumn]?.trim();
            if (!t || !c) continue;
            const dt = idxType !== -1 ? (parts[idxType]?.trim() || '') : '';
            if (!tables[t]) tables[t] = { columns: [], types: {} };
            if (!tables[t].columns.includes(c)) tables[t].columns.push(c);
            tables[t].types[c] = dt;
          }
            setSchemaMeta({ tables });
        } catch {/* ignore parse errors */}
      };
      reader.readAsText(file);
    }
  };

  const handleLimitChange = (value: string) => {
    setLimit(value);
    if (value !== 'custom') {
      setCustomLimit('');
    }
  };

  const handleCustomLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow positive numbers
    if (value === '' || /^\d+$/.test(value)) {
      setCustomLimit(value);
    }
  };

  const getEffectiveLimit = (): string | null => {
    if (limit === 'all') return null;
    if (limit === 'custom') return customLimit || null;
    return limit;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!schemaFile) {
      setError('Please select a schema file');
      return;
    }
    
    if (!request.trim()) {
      setError('Please enter a request');
      return;
    }

    if (limit === 'custom' && (!customLimit || parseInt(customLimit) <= 0)) {
      setError('Please enter a valid custom limit');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const effectiveLimit = getEffectiveLimit();
      console.log('Making API call with:', { file: schemaFile.name, request: request.trim(), dialect, limit: effectiveLimit });
      
      const sqlResult = await generateSQL(schemaFile, request.trim(), dialect, effectiveLimit);
      setResult(sqlResult);
      
      // Initialize edited SQL with the generated query
      setEditedSQL(sqlResult.sql || '');
      setIsSQLValid(true);
      setSQLValidationErrors([]);
    } catch (err) {
      console.error('API Error:', err);
      let errorMessage = 'An unexpected error occurred';
      
      if (err instanceof Error) {
        if (err.message.includes('Server returned empty response')) {
          errorMessage = 'The server is not responding. Please check if the application is properly deployed.';
        } else if (err.message.includes('Invalid JSON response')) {
          errorMessage = 'The server returned an invalid response. This might be a deployment issue.';
        } else if (err.message.includes('Failed to fetch')) {
          errorMessage = 'Cannot connect to the server. Please check your internet connection.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSchemaFile(null);
    setRequest('');
    setDialect('postgres');
    setLimit('100');
    setCustomLimit('');
    setResult(null);
    setError(null);
    setEditedSQL('');
    setIsSQLValid(true);
    setSQLValidationErrors([]);
  setSchemaMeta(null);
    // Reset file input
    const fileInput = document.getElementById('schema-file') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleRevalidate = async () => {
    if (!schemaFile || !editedSQL.trim()) {
      setError('Cannot revalidate: missing schema file or SQL query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const effectiveLimit = getEffectiveLimit();
      console.log('Revalidating edited SQL:', { sql: editedSQL.trim(), dialect, limit: effectiveLimit });
      
      // Create a new request that includes the edited SQL for validation
      const validationRequest = `Please validate this SQL query: ${editedSQL.trim()}`;
      const validationResult = await generateSQL(schemaFile, validationRequest, dialect, effectiveLimit);
      
      // Update the result with validation info but keep the edited SQL
      setResult({
        ...validationResult,
        sql: editedSQL.trim()
      });
      
    } catch (err) {
      console.error('Revalidation Error:', err);
      let errorMessage = 'Failed to revalidate SQL query';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
            <Database className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold odpm-heading-gradient">
              SQL Query Builder
            </h1>
            <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          </div>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            Transform your CSV schema into powerful SQL queries using natural language and AI
          </p>
        </div>

        <DebugPanel />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {/* Input Form */}
          <div className="space-y-4 sm:space-y-6">
            <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Upload className="h-4 w-4 sm:h-5 sm:w-5" />
                  Configuration
                </CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Upload your schema and configure your query parameters
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label htmlFor="schema-file" className="flex items-center gap-2 font-medium">
                      <FileText className="h-4 w-4" />
                      Schema CSV File
                    </Label>
                    <Input
                      id="schema-file"
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleFileChange}
                      className="cursor-pointer file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                    />
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      Expected headers: table_name, column_name, data_type. 
                      Don't worry if your headers are different - we'll use AI to map them! 🤖
                    </p>
                    {schemaFile && (
                      <div className="flex items-center gap-2 text-sm p-3 rounded-lg panel bordered" style={{boxShadow:'var(--shadow-sm)', background:'linear-gradient(90deg, rgba(152,195,121,0.08), rgba(97,175,239,0.05))'}}>
                        <CheckCircle className="h-4 w-4 shrink-0 text-success" />
                        <span className="font-medium text-success">{schemaFile.name}</span>
                        <span className="text-muted-foreground">uploaded successfully</span>
                      </div>
                    )}
                  </div>

                  {/* SQL Dialect */}
                  <div className="space-y-2">
                    <Label htmlFor="dialect" className="flex items-center gap-2 font-medium">
                      <Settings className="h-4 w-4" />
                      SQL Dialect
                    </Label>
                    <Select value={dialect} onValueChange={setDialect}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select SQL dialect" />
                      </SelectTrigger>
                      <SelectContent>
                        {SQL_DIALECTS.map(({ value, label }) => (
                          <SelectItem key={value} value={value} className="py-2">
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Result Limit */}
                  <div className="space-y-2">
                    <Label htmlFor="limit" className="font-medium">Result Limit</Label>
                    <Select value={limit} onValueChange={handleLimitChange}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select result limit" />
                      </SelectTrigger>
                      <SelectContent>
                        {LIMIT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value} className="py-2">
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {limit === 'custom' && (
                      <Input
                        type="number"
                        value={customLimit}
                        onChange={(e) => handleCustomLimitChange(e)}
                        placeholder="Enter custom limit"
                        min="1"
                        className="mt-2 h-11"
                      />
                    )}
                  </div>

                  {/* Request Textarea */}
                  <div className="space-y-2">
                    <Label htmlFor="request" className="font-medium">Your Request</Label>
                    <Textarea
                      id="request"
                      value={request}
                      onChange={(e) => setRequest(e.target.value)}
                      placeholder="Describe what you want to query in plain English..."
                      rows={4}
                      className="min-h-[120px] resize-none"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      type="submit" 
                      disabled={loading || !schemaFile || !request.trim()} 
                      className="flex-1 h-11 font-medium"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate SQL
                        </>
                      )}
                    </Button>
                    <Button 
                      type="button" 
                      onClick={handleReset} 
                      variant="outline" 
                      className="h-11 font-medium sm:flex-initial"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>

                  {/* Progress Bar */}
                  {loading && (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm font-medium">
                        <span>Processing...</span>
                        <span>Please wait</span>
                      </div>
                      <Progress value={50} className="h-2 animate-pulse" />
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="space-y-4 sm:space-y-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="border-red-200 dark:border-red-800">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="font-semibold">Error</AlertTitle>
                <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
              </Alert>
            )}

            {/* Results */}
            {result && (
              <Tabs defaultValue="sql" className="space-y-4">
                <TabsList className="grid w-full grid-cols-4 h-auto p-1">
                  <TabsTrigger value="sql" className="text-xs sm:text-sm py-2">SQL Query</TabsTrigger>
                  <TabsTrigger value="details" className="text-xs sm:text-sm py-2">Details</TabsTrigger>
                  <TabsTrigger value="validation" className="text-xs sm:text-sm py-2">Validation</TabsTrigger>
                  <TabsTrigger value="mapping" className="text-xs sm:text-sm py-2">Mapping</TabsTrigger>
                </TabsList>

                <TabsContent value="sql" className="space-y-4">
                  <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
                    <CardHeader className="pb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg sm:text-xl">Generated SQL Query</CardTitle>
                          <CardDescription className="text-sm">
                            Dialect: <strong className="text-foreground">{result.dialect}</strong>
                          </CardDescription>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                          <Button 
                            onClick={() => navigator.clipboard.writeText(editedSQL)}
                            variant="outline"
                            size="sm"
                            disabled={!editedSQL.trim()}
                            className="h-9"
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </Button>
                          <Button 
                            onClick={handleRevalidate}
                            variant="outline"
                            size="sm"
                            disabled={loading || !editedSQL.trim()}
                            className="h-9"
                          >
                            {loading ? 'Validating...' : 'Revalidate'}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="monaco-editor-container">
                        <SQLEditor
                          initialValue={editedSQL}
                          dialect={result.dialect}
                          schemaMeta={schemaMeta}
                          onValidate={(isValid: boolean, errors: string[]) => {
                            setIsSQLValid(isValid);
                            setSQLValidationErrors(errors);
                          }}
                          onValueChange={setEditedSQL}
                        />
                      </div>
                      
                      {!isSQLValid && sqlValidationErrors.length > 0 && (
                        <Alert variant="destructive" className="mt-4 border-red-200 dark:border-red-800">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle className="font-semibold">SQL Validation Issues</AlertTitle>
                          <AlertDescription>
                            <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                              {sqlValidationErrors.map((error, index) => (
                                <li key={index} className="leading-relaxed">{error}</li>
                              ))}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="details" className="space-y-4">
                  {result.rationale && (
                    <Card className="shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Rationale</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">{result.rationale}</p>
                      </CardContent>
                    </Card>
                  )}

                  {result.assumptions.length > 0 && (
                    <Card className="shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Assumptions Made</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                          {result.assumptions.map((assumption, index) => (
                            <li key={index} className="leading-relaxed">{assumption}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {result.follow_up && (
                    <Card className="shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Follow-up Question</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">{result.follow_up}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="validation" className="space-y-4">
                  <Card className="shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Validation Results</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {result.validation.tables_used.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3">Tables Used</h4>
                          <div className="flex flex-wrap gap-2">
                              {result.validation.tables_used.map((table, index) => (
                                <div key={index} className="odpm-chip odpm-chip-primary">{table}</div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {result.validation.columns_used.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3">Columns Used</h4>
                          <div className="flex flex-wrap gap-2">
                            {result.validation.columns_used.map((column, index) => (
                              <div key={index} className="odpm-chip odpm-chip-success">{column}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {result.validation.schema_mismatches.length > 0 && (
                        <Alert variant="destructive" className="border-red-200 dark:border-red-800">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle className="font-semibold">Schema Issues</AlertTitle>
                          <AlertDescription>
                            <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                              {result.validation.schema_mismatches.map((mismatch, index) => (
                                <li key={index} className="leading-relaxed">{mismatch}</li>
                              ))}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      )}

                      {result.parameters.limit && (
                        <div className="p-4 bg-muted/50 rounded-lg border">
                          <h4 className="font-medium mb-3">Query Parameters</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-muted-foreground">Limit:</span> 
                              <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono text-xs">
                                {result.parameters.limit}
                              </span>
                            </div>
                            {result.parameters.order_by && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-muted-foreground">Order By:</span> 
                                <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono text-xs">
                                  {result.parameters.order_by}
                                </span>
                              </div>
                            )}
                            {result.parameters.filters.length > 0 && (
                              <div>
                                <span className="font-medium text-muted-foreground">Filters:</span>
                                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                                  {result.parameters.filters.map((filter, index) => (
                                    <li key={index} className="text-muted-foreground">{filter}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="mapping" className="space-y-4">
                  {result.header_mapping?.mapping_used ? (
                    <Card className="shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Sparkles className="h-5 w-5 text-warning" />
                          AI Header Mapping Applied
                        </CardTitle>
                        <CardDescription>
                          Your CSV headers were automatically mapped using intelligent AI processing
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h4 className="font-medium mb-3">Original Headers</h4>
                          <div className="flex flex-wrap gap-2">
                            {result.header_mapping.original_headers.map((header, index) => (
                              <div key={index} className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-700">
                                {header}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-medium mb-3">Mapped To</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div className="flex flex-col sm:flex-row sm:justify-between p-3 panel rounded-lg">
                              <span className="font-medium text-primary mb-1 sm:mb-0">Table Name:</span>
                              <span className="font-mono text-xs odpm-chip odpm-chip-primary" style={{background:'transparent',border:'none',padding:'2px 6px'}}>
                                {result.header_mapping.mapped_headers.table_name}
                              </span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between p-3 panel rounded-lg">
                              <span className="font-medium text-success mb-1 sm:mb-0">Column Name:</span>
                              <span className="font-mono text-xs odpm-chip odpm-chip-success" style={{background:'transparent',border:'none',padding:'2px 6px'}}>
                                {result.header_mapping.mapped_headers.column_name}
                              </span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between p-3 panel rounded-lg">
                              <span className="font-medium text-accent mb-1 sm:mb-0">Data Type:</span>
                              <span className="font-mono text-xs odpm-chip odpm-chip-accent" style={{background:'transparent',border:'none',padding:'2px 6px'}}>
                                {result.header_mapping.mapped_headers.data_type}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="shadow-sm">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <CheckCircle className="h-5 w-5 text-success" />
                          Standard Headers Detected
                        </CardTitle>
                        <CardDescription>
                          Your CSV file already has the expected headers format
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          No header mapping was needed. Your CSV file follows the expected format with 
                          table_name, column_name, and data_type columns.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;